import React, { lazy, useMemo } from 'react'
import { useRoutes, Navigate } from 'react-router-dom'
import {
  EMAIL_PAGES,
  ID_TO_PAGE_MAP,
  PageId,
  PURCHASE_PAGES,
  PURCHASE_PAGES_MAP,
} from 'page-constants'
import { useSelector } from 'react-redux'
import { selectCurrentVariantCohort } from 'root-redux/selects/common'
import { getPathFromPageId } from 'helpers/getPathFromPageId'
import {
  selectTestEnvironmentQueryParam,
  selectUUID,
} from 'root-redux/selects/user'
import { ProtectedRoute } from 'components/ProtectedRoute'
import { useUserStatus } from 'hooks/useUserStatus'
import { SubscribeFlow } from 'components/SubscribeFlow'
import { useGetPageInfo } from 'hooks/useGetPageInfo'
import { Cohort } from 'root-constants'
import { AnimationProvider } from 'components/AnimationProvider'
import { Result } from './pages/result'
import { Download } from './pages/download'

const StripeProvider = lazy(() => import('components/StripeProvider'))

export const RouteList: React.FC = () => {
  const availableRoute = useUserStatus()
  const { currentVariantPageList } = useGetPageInfo()
  const cohort = useSelector(selectCurrentVariantCohort)
  const uuid = useSelector(selectUUID)
  const testEnvironmentQueryParam = useSelector(selectTestEnvironmentQueryParam)

  const isPurchase = useMemo(
    () => availableRoute === PURCHASE_PAGES_MAP[cohort as Cohort],
    [availableRoute, cohort],
  )

  const firstPagePath = useMemo(
    () =>
      getPathFromPageId({
        pageId: currentVariantPageList[0].id,
        cohort,
        uuid,
        testEnvironmentQueryParam,
      }),
    [currentVariantPageList, cohort, uuid, testEnvironmentQueryParam],
  )

  return useRoutes([
    { index: true, element: <Navigate to={firstPagePath} /> },
    {
      element: <SubscribeFlow />,
      children: currentVariantPageList.map(({ id: pageId }, index, arr) => {
        const CurrentPage = ID_TO_PAGE_MAP[pageId]
        const nextPagePath = getPathFromPageId({
          pageId: arr[index + 1]?.id,
          cohort,
          uuid,
          testEnvironmentQueryParam,
        })

        return {
          path: pageId,
          element: (
            <ProtectedRoute isAvailable={!availableRoute}>
              <AnimationProvider>
                <CurrentPage pageId={pageId} nextPagePath={nextPagePath} />
              </AnimationProvider>
            </ProtectedRoute>
          ),
        }
      }),
    },
    ...EMAIL_PAGES.map(({ id }) => {
      const EmailPage = ID_TO_PAGE_MAP[id]

      return {
        path: id,
        element: (
          <AnimationProvider>
            <EmailPage nextPagePath="/" pageId={id} />
          </AnimationProvider>
        ),
      }
    }),
    {
      path: PageId.RESULT,
      element: (
        <AnimationProvider>
          <Result />
        </AnimationProvider>
      ),
    },
    ...PURCHASE_PAGES.map(({ id }) => {
      const PurchasePage = ID_TO_PAGE_MAP[id]

      return {
        path: id,
        element: (
          <ProtectedRoute isAvailable={isPurchase}>
            <StripeProvider>
              <AnimationProvider>
                <PurchasePage nextPagePath="/" pageId={id} />
              </AnimationProvider>
            </StripeProvider>
          </ProtectedRoute>
        ),
      }
    }),
    {
      path: PageId.DOWNLOAD,
      element: (
        <ProtectedRoute isAvailable={availableRoute === PageId.DOWNLOAD}>
          <AnimationProvider>
            <Download />
          </AnimationProvider>
        </ProtectedRoute>
      ),
    },
    { path: '*', element: <Navigate to={firstPagePath} /> },
  ])
}
